//deps
import React, { memo, ReactElement, useCallback, useMemo, useState } from "react";

//defs
import { AddMoneyAndDelinkWalletProps } from "@payment-ui/interfaces";

//internal-deps
import { InputFieldv4 } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

//styles
import styles from "./styles.scss";

//constants
import { TEXTS } from "@payment-ui/constants/index";

//helpers
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import { getAmountWithoutRupeeSymbol, getAmountWithRupeeSymbol } from "@payment-ui/helpers/Wallet";

//data-test-id
import { ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface AddMoneyProps {
    onAddMoney: AddMoneyAndDelinkWalletProps["onAddMoney"];
}

const inputAttr = {
    type: "tel",
    name: "addMoney",
    id: "addMoney",
    "data-testid": ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.ADD_MONEY_IMPUT,
};

function AddMoney(props: AddMoneyProps): ReactElement {
    const [amount, setAmount] = useState("");

    const { onAddMoney } = props;

    const onInputChange = useCallback((amount: string): void => {
        const amountWithoutRupeeSymbol = getAmountWithoutRupeeSymbol(amount);
        if (isNaN(amountWithoutRupeeSymbol)) {
            return;
        }
        if (amountWithoutRupeeSymbol > 10000000000) {
            return;
        }
        const newAmount = amountWithoutRupeeSymbol === 0 ? "" : getAmountWithRupeeSymbol(amountWithoutRupeeSymbol);

        setAmount(newAmount);
    }, []);

    const isAddMoneyCtaDisabled = !amount.length;

    const onAddMoneyClick = useCallback(() => {
        if (isAddMoneyCtaDisabled) {
            return;
        }

        onAddMoney(getAmountWithoutRupeeSymbol(amount));
    }, [amount, isAddMoneyCtaDisabled, onAddMoney]);

    const addMoneyText = useMemo(() => capitalizeFirstLetter(TEXTS.ADD_MONEY), []);

    return (
        <>
            <InputFieldv4
                className={styles.addMoneyInput}
                label={TEXTS.ENTER_AMOUNT}
                onChange={onInputChange}
                attrs={inputAttr}
                value={amount}
            />
            <div className={styles.addMoneyCta}>
                <SuperButton
                    buttonType="primary"
                    buttonSize="large"
                    variant="fullwidth"
                    isDisabled={isAddMoneyCtaDisabled}
                    onClick={onAddMoneyClick}
                    data-testid={ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.ADD_MONEY_CTA}
                >
                    {addMoneyText}
                </SuperButton>
            </div>
        </>
    );
}

export default memo(AddMoney);
